<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import { reactive } from "vue"
import AppShell from "../Layout/AppShell.vue"
import { UiButton, UiDataTable } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"
import type { UiDataTableColumn, UiDataTableFilter } from "@/types/ui"

type UserCompany = {
  id: number
  name: string
}

type UserRow = {
  id: number
  name: string
  email: string
  role_key: string
  role_label: string
  companies: UserCompany[]
  confirmed: boolean
  can_edit: boolean
  can_destroy: boolean
  can_send_password_reset: boolean
  can_resend_confirmation: boolean
}

const props = defineProps<{
  users: UserRow[]
  companies: Array<{ id: number; name: string }>
  role_options: Array<{ label: string; value: string }>
  query: {
    q?: string
    company_id?: string
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
  can_create: boolean
}>()

const { confirm } = useConfirmation()
const notify = useNotifications()

const columns: UiDataTableColumn[] = [
  { key: "name", label: "Nome" },
  { key: "email", label: "Email" },
  { key: "role_label", label: "Perfil" },
  { key: "companies", label: "Empresas" },
  { key: "confirmed", label: "Confirmado", align: "center" },
  { key: "actions", label: "Acoes", align: "right" },
]

const filters: UiDataTableFilter[] = [
  {
    key: "company_id",
    label: "Empresa",
    options: props.companies.map((company) => ({ label: company.name, value: company.id })),
  },
]

const selectedFilters = reactive<Record<string, string | number | "">>({
  company_id: props.query.company_id ?? "",
})

function companyListLabel(user: UserRow): string {
  if (user.companies.length === 0) return "-"
  return user.companies.map((company) => company.name).join(", ")
}

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    q: String(overrides.q ?? props.query.q ?? ""),
    company_id: String(overrides.company_id ?? selectedFilters.company_id ?? ""),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/users", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function updateFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  fetchWithQuery({ [key]: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

async function destroyUser(id: number) {
  const ok = await confirm({
    title: "Excluir usuario",
    message: "Deseja excluir este usuario?",
    type: "danger",
    confirmText: "Excluir",
  })
  if (!ok) return

  router.delete(`/users/${id}`, {
    preserveScroll: true,
    onSuccess: () => notify.success("Usuario removido com sucesso"),
  })
}

function sendPasswordReset(id: number) {
  router.post(`/users/${id}/send_password_reset`, {}, {
    preserveScroll: true,
    onSuccess: () => notify.success("Email de recuperacao enviado"),
  })
}

function resendConfirmation(id: number) {
  router.post(`/users/${id}/resend_confirmation`, {}, {
    preserveScroll: true,
    onSuccess: () => notify.success("Email de confirmacao reenviado"),
  })
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Usuarios</h1>
        <p class="mt-2 text-sm text-slate-600">Gerencie acessos, perfil e comunicacoes de acesso por email.</p>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="users"
        :search="query.q ?? ''"
        :filters="filters"
        :selected-filters="selectedFilters"
        :pagination="pagination"
        @update:search="updateSearch"
        @update:filter="updateFilter"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #toolbar-actions>
          <Link
            v-if="can_create"
            href="/users/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Novo usuario
          </Link>
        </template>

        <template #cell="{ row, column, value }">
          <div v-if="column.key === 'actions'" class="flex flex-wrap justify-end gap-2">
            <Link
              v-if="(row as UserRow).can_edit"
              :href="`/users/${(row as UserRow).id}/edit`"
              class="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Editar
            </Link>

            <UiButton
              v-if="(row as UserRow).can_send_password_reset"
              size="sm"
              variant="secondary"
              @click="sendPasswordReset((row as UserRow).id)"
            >
              Recuperacao
            </UiButton>

            <UiButton
              v-if="(row as UserRow).can_resend_confirmation"
              size="sm"
              variant="secondary"
              @click="resendConfirmation((row as UserRow).id)"
            >
              Reenviar confirmacao
            </UiButton>

            <UiButton v-if="(row as UserRow).can_destroy" size="sm" variant="danger" @click="destroyUser((row as UserRow).id)">
              Excluir
            </UiButton>
          </div>

          <span v-else-if="column.key === 'companies'">{{ companyListLabel(row as UserRow) }}</span>
          <span v-else-if="column.key === 'confirmed'">
            {{ (row as UserRow).confirmed ? "Sim" : "Nao" }}
          </span>
          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>
    </div>
  </AppShell>
</template>


